import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Stack,
  Typography,
  useTheme
} from '@mui/material';
import { api, apiGlobal } from '../services/api';
import { useGroup } from '../contexts/GroupContext.jsx';
import { CenteredLoader } from '../components/LoadingIndicator';
import { useToast } from '../components/ToastNotification';
import { PageHero, Badge } from '../components/ds';

const errMessage = (err) => err?.body?.error || err?.message || 'Something went wrong';

// Role -> Badge variant (DESIGN_SYSTEM.md section 8.3), same mapping as Members.
const roleBadgeVariant = (role) =>
  role === 'owner' ? 'warning' : role === 'editor' ? 'interactive' : 'neutral';

const InvitationAccept = ({ isAuthReady = false }) => {
  const { inviteId } = useParams();
  const navigate = useNavigate();
  const { reload: reloadGroups, setActiveGroupId } = useGroup();
  const { showError, showSuccess } = useToast();
  const theme = useTheme();
  const { palette, ds } = theme;

  const [invite, setInvite] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const res = await apiGlobal.get(`/invites/${encodeURIComponent(inviteId)}`);
      setInvite(res?.invite || null);
    } catch (err) {
      const msg =
        err.status === 403 ? 'This invite was sent to a different email address.'
        : err.status === 404 ? 'Invite not found. It may have been revoked.'
        : errMessage(err);
      setError(msg);
    } finally {
      setLoading(false);
    }
  }, [inviteId]);

  useEffect(() => {
    if (isAuthReady && inviteId) load();
  }, [isAuthReady, inviteId, load]);

  const handleAccept = async () => {
    if (!invite) return;
    setBusy(true);
    try {
      await api.post(invite.groupId, `/invites/${encodeURIComponent(invite.inviteId)}/accept`);
      showSuccess(`Joined ${invite.groupName || 'workspace'}`);
      await reloadGroups();
      setActiveGroupId(invite.groupId);
      window.dispatchEvent(new CustomEvent('budgetrecon:invites-changed'));
      setDone('accepted');
      setTimeout(() => navigate('/'), 1200);
    } catch (err) {
      const status = err.status;
      if (status === 409) showError('Invite already actioned.');
      else if (status === 410) showError('Invite expired.');
      else if (status === 403) showError('Invite is addressed to a different email.');
      else showError(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  const handleDecline = async () => {
    if (!invite) return;
    setBusy(true);
    try {
      await api.post(invite.groupId, `/invites/${encodeURIComponent(invite.inviteId)}/decline`);
      showSuccess('Invite declined');
      window.dispatchEvent(new CustomEvent('budgetrecon:invites-changed'));
      setDone('declined');
      setTimeout(() => navigate('/'), 1200);
    } catch (err) {
      showError(errMessage(err));
    } finally {
      setBusy(false);
    }
  };

  if (!isAuthReady) return <CenteredLoader message="Initializing..." />;
  if (loading) return <CenteredLoader message="Loading invite..." />;
  if (error) return (
    <Box sx={{ p: 3 }}>
      <Alert severity="error">{error}</Alert>
      <Button sx={{ mt: 2 }} onClick={() => navigate('/')}>Back to Dashboard</Button>
    </Box>
  );
  if (!invite) return null;

  const expired = invite.expiresAt && new Date(invite.expiresAt) < new Date();
  const alreadyActioned = invite.status && invite.status !== 'pending';
  const heroValue = done === 'accepted' ? 'Joined!'
    : done === 'declined' ? 'Declined'
    : 'Invitation';

  return (
    <Box sx={{ pb: 8 }}>
      <PageHero
        label="Workspace invite"
        value={heroValue}
        context={done ? 'Redirecting...' : undefined}
      />

      <Box sx={{ px: 1, mt: 2, maxWidth: 640, mx: 'auto' }}>
        <Box
          sx={{
            backgroundColor: 'surface.raised',
            border: `1px solid ${palette.divider}`,
            borderRadius: `${ds.radius.container}px`,
            p: `${ds.space[6]}px`,
          }}
        >
          {done ? (
            <Typography sx={{ color: 'text.secondary' }}>Redirecting...</Typography>
          ) : (
            <>
              <Stack spacing={1.5} sx={{ mb: 3 }}>
                <Typography>
                  You have been invited to join{' '}
                  <strong>{invite.groupName || invite.groupId}</strong>
                  {' '}as{' '}
                  <Badge variant={roleBadgeVariant(invite.role)} sx={{ verticalAlign: 'middle' }}>
                    {invite.role}
                  </Badge>.
                </Typography>
                {(invite.invitedByName || invite.invitedByEmail) && (
                  <Typography variant="body2" color="text.secondary">
                    Invited by: {invite.invitedByName || invite.invitedByEmail}
                    {invite.invitedByName && invite.invitedByEmail && ` <${invite.invitedByEmail}>`}
                  </Typography>
                )}
                {invite.expiresAt && (
                  <Typography variant="body2" color="text.secondary">
                    Expires: {new Date(invite.expiresAt).toLocaleString()}
                  </Typography>
                )}
              </Stack>

              {alreadyActioned && (
                <Alert severity="info" sx={{ mb: 2 }}>
                  This invite is already {invite.status}.
                </Alert>
              )}
              {expired && !alreadyActioned && (
                <Alert severity="warning" sx={{ mb: 2 }}>
                  This invite has expired.
                </Alert>
              )}

              <Stack direction="row" spacing={2}>
                <Button
                  variant="contained"
                  onClick={handleAccept}
                  disabled={busy || expired || alreadyActioned}
                >
                  Accept
                </Button>
                <Button
                  variant="outlined"
                  onClick={handleDecline}
                  disabled={busy || alreadyActioned}
                >
                  Decline
                </Button>
                <Button onClick={() => navigate('/')} disabled={busy}>
                  Later
                </Button>
              </Stack>
            </>
          )}
        </Box>
      </Box>
    </Box>
  );
};

export default InvitationAccept;
